// client/src/pages/secure/AgentDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canSeePersonFile } from '../../hooks/useCurrentUser';
import DocumentManager from '../../components/secure/DocumentManager';
import CeRecords from '../../components/secure/CeRecords';
import FormsPanel from '../../components/secure/FormsPanel';

// --- Edit Agent Modal Component ---
const EditAgentModal = ({ isOpen, onClose, onAgentUpdated, agent }) => {
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', license_number: '', license_type: 'unknown',
        license_expiration_date: '', license_states: '', email: '', phone: '', status: '',
        hire_date: '', termination_date: '', is_managing_broker: false,
        street_address: '', city: '', state: '', zip_code: '',
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const d10 = (v) => (v ? String(v).slice(0, 10) : '');

    useEffect(() => {
        if (agent) {
            setFormData({
                firstName: agent.first_name || '',
                lastName: agent.last_name || '',
                license_number: agent.license_number || '',
                license_type: agent.license_type || 'unknown',
                license_expiration_date: d10(agent.license_expiration_date),
                license_states: agent.license_states || '',
                email: agent.email || '',
                phone: agent.phone || '',
                status: agent.status || 'Active',
                hire_date: d10(agent.hire_date),
                termination_date: d10(agent.termination_date),
                is_managing_broker: !!agent.is_managing_broker,
                street_address: agent.street_address || '',
                city: agent.city || '',
                state: agent.state || '',
                zip_code: agent.zip_code || '',
            });
        }
    }, [agent]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const updatedAgent = await apiClient.put(`/api/agents/${agent.agent_id}`, formData);
            onAgentUpdated(updatedAgent);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to update agent.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Edit Agent Details</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="p-2 border rounded w-full" />
                    <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="p-2 border rounded w-full" />
                    <input name="license_number" value={formData.license_number} onChange={handleChange} placeholder="License Number" required className="p-2 border rounded w-full" />
                    <p className="text-xs text-slate-500 -mt-2">
                        Illinois licences look like 475.123456. Where the number is not known yet, a value
                        beginning UNVERIFIED- records the gap honestly instead of inventing a number.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="license_type" className="block text-sm font-medium text-slate-700">License Type</label>
                            <select id="license_type" name="license_type" value={formData.license_type} onChange={handleChange} className="mt-1 p-2 border rounded w-full">
                                <option value="unknown">Not recorded</option>
                                <option value="broker">Broker</option>
                                <option value="managing_broker">Managing Broker</option>
                                <option value="residential_leasing_agent">Residential Leasing Agent</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="license_expiration_date" className="block text-sm font-medium text-slate-700">License Expiration</label>
                            <input id="license_expiration_date" name="license_expiration_date" type="date" value={formData.license_expiration_date} onChange={handleChange} className="mt-1 p-2 border rounded w-full" />
                            <label htmlFor="license_states" className="block text-sm font-medium text-slate-700 mt-2">Licensed in (states)</label>
                            <input id="license_states" name="license_states" value={formData.license_states} onChange={handleChange} placeholder="IL or IL,FL — blank = this site's state" className="mt-1 p-2 border rounded w-full" />
                        </div>
                    </div>
                    <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" className="p-2 border rounded w-full" />
                    <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone" className="p-2 border rounded w-full" />

                    {/* Sponsorship dates. 1450.735(b) wants the employment terms recorded and
                        1450.755 wants the file kept, so when a licensee started and ended is
                        part of the record rather than a detail. */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="hire_date" className="block text-sm font-medium text-slate-700">Sponsorship Start</label>
                            <input id="hire_date" name="hire_date" type="date" value={formData.hire_date} onChange={handleChange} className="mt-1 p-2 border rounded w-full" />
                        </div>
                        <div>
                            <label htmlFor="termination_date" className="block text-sm font-medium text-slate-700">Termination Date</label>
                            <input id="termination_date" name="termination_date" type="date" value={formData.termination_date} onChange={handleChange} className="mt-1 p-2 border rounded w-full" />
                        </div>
                    </div>

                    <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded w-full">
                        <option value="Active">Active</option>
                        <option value="Pending">Pending</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Terminated">Terminated</option>
                    </select>
                    {formData.status === 'Terminated' && !formData.termination_date && (
                        <p className="text-amber-700 text-sm">
                            A terminated licensee needs a termination date before this will save.
                        </p>
                    )}

                    <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" name="is_managing_broker"
                            checked={formData.is_managing_broker}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_managing_broker: e.target.checked }))} />
                        Managing broker
                    </label>

                    <div className="grid grid-cols-2 gap-4">
                        <input name="street_address" value={formData.street_address} onChange={handleChange} placeholder="Street Address" className="p-2 border rounded w-full col-span-2" />
                        <input name="city" value={formData.city} onChange={handleChange} placeholder="City" className="p-2 border rounded w-full" />
                        <div className="grid grid-cols-2 gap-2">
                            <input name="state" value={formData.state} onChange={handleChange} placeholder="State" maxLength={2} className="p-2 border rounded w-full" />
                            <input name="zip_code" value={formData.zip_code} onChange={handleChange} placeholder="ZIP" className="p-2 border rounded w-full" />
                        </div>
                    </div>
                    {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Main Agent Detail Page Component ---
const AgentDetailPage = () => {
    const { id } = useParams();
    const [agent, setAgent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const apiClient = useApiClient();

    const fetchAgentDetails = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await apiClient.get(`/api/agents/${id}`);
            setAgent(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch agent details.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]);

    useEffect(() => {
        fetchAgentDetails();
    }, [fetchAgentDetails]);

    const handleAgentUpdated = (updatedAgent) => {
        setAgent(updatedAgent);
    };

    if (isLoading) return <div className="p-8">Loading agent details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;

    return (
        <>
            <EditAgentModal 
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onAgentUpdated={handleAgentUpdated}
                agent={agent}
            />
            <div className="space-y-8 p-4 md:p-8">
                <div className="mb-6">
                    <Link to="/dashboard/agents" className="text-indigo-600 hover:text-indigo-900">
                        &larr; Back to Agent List
                    </Link>
                </div>

                {agent && (
                    <div className="space-y-8">
                        {/* Agent Header */}
                        <div className="bg-white p-8 rounded-lg shadow-md">
                            <div className="flex justify-between items-start">
                                <div className="flex items-center space-x-6">
                                    <img 
                                        src={'/default-profile.png'} 
                                        alt={`${agent.name}'s profile`}
                                        className="h-24 w-24 rounded-full object-cover"
                                    />
                                    <div>
                                        <h1 className="text-3xl font-bold text-slate-800">{agent.name}</h1>
                                        <p className="text-slate-600">License: {agent.license_number}</p>
                                        <p className="text-slate-600">License Expires: {agent.license_expiration_date ? new Date(agent.license_expiration_date).toLocaleDateString() : 'N/A'}</p>
                                        <p className="text-slate-600">{agent.email} | {agent.phone || 'N/A'}</p>
                                        <p className="mt-2">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${agent.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {agent.status}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <button onClick={() => setIsEditModalOpen(true)} className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200">
                                    Edit Agent
                                </button>
                            </div>
                        </div>

                        {/* An agent's documents (licence, ICA, W-9) open to the agent, the office
                            manager, the managing broker and an examiner; others see a note
                            instead of an error. */}
                        {canSeePersonFile(getCurrentUser(), { agentId: id }) ? (
                            <DocumentManager
                                relatedId={id}
                                relatedType="agent"
                            />
                        ) : (
                            <p className="mt-8 text-sm text-slate-500">This agent's documents are visible to the agent, the office manager and the managing broker.</p>
                        )}

                        <CeRecords agentId={id} />

                        {/* The sponsorship contract, the compensation schedule and the
                            policy acknowledgements — signed with the same evidence as a
                            client document, rather than the typed name and timestamp the
                            Policy Center used to record on its own. */}
                        <div className="mt-8">
                            <FormsPanel
                                subjectType="agent"
                                subjectId={id}
                                subjectName={agent ? `${agent.first_name || ''} ${agent.last_name || ''}`.trim() : null}
                            />
                        </div>

                        {/* Placeholder for other sections */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                             <h3 className="text-xl font-bold text-slate-800 mb-4">Agent Activity</h3>
                             <p className="text-slate-500">Sections for agent's transactions, teams, and other related information will be displayed here.</p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AgentDetailPage;